import React, { useState } from "react";
import { Box, Button, FormControl, FormControlLabel, Radio, RadioGroup, Table, TableBody, TableCell, TableRow, TextField, Typography, useTheme } from "@mui/material";
import { ExportStatus, Sale, SalesExport, ThemeColor, UserRole } from "../../../system/Domain";
import ListItemAction from "Components/ListItemAction";
import ListItemChip from "Components/ListItemChip";
import ListItemChips from "Components/ListItemChips";
import { Backspace, Check, Clear, Delete, ThumbDown, ThumbDownAlt, ThumbUpAlt } from "@mui/icons-material";
import axios from "axios";
import formatDateTime from "system/formatDateTime";
import useUser from "system/useUser";
import _ from "lodash";
import { Colgroup } from "Components/BreakpointStyledComponents";
import { translateExportStatus } from "../../../system/translate";
import TextBlock from "../../../Components/Layout/TextBlock";
import List from "../../../Components/List";
import ListItem from "../../../Components/ListItem";
import ListItemActions from "../../../Components/ListItemActions";
import ListItemContent from "../../../Components/ListItemContent";

interface Props {
	sale: Sale
	saleExport: SalesExport
	setSale: (s: Sale) => void
}

const getStatusColor = (status: ExportStatus) => {
	switch (status) {
		case "AwaitingDocuments":
			return "info";
		case "SecondApproval":
			return "success";
		case "Rejected":
		case "Cancelled":
			return "error";
		default: // "ReadyForApproval" | "FirstApproval"
			return "warning";
	}
};

const StatusHistoryList = ({ history }) => {
	const theme = useTheme();

	const decorateStatus = (status: ExportStatus) => {
		const statusColor = getStatusColor(status);
		const paletteColor = theme.palette[statusColor];

		return {
			fontSize: "0.7rem",
			color: paletteColor.light
		};
	};

	return (
		<Box>
			<Typography>Statushistorie</Typography>
			<Table>
				<Colgroup sm md lg xl>
					<col width="20%" />
					<col width="auto" />
					<col width="0" />
				</Colgroup>
				<TableBody>
					{history.map((h, i) => (
						<TableRow sx={{
							"& td": {
								borderBottom: "none",
								borderTop: "0",
								padding: "0",
								paddingBottom: 1,
								verticalAlign: "top",
								fontSize: "0.55rem",
								lineHeight: "1.15rem",
								color: theme.palette.text.secondary
							}
						}} key={i}>
							<TableCell>
								{formatDateTime(h.date)}
							</TableCell>
							<TableCell>
								<TextBlock
									primary={
										<Box sx={{ ...decorateStatus(h.value.status) }}>
											{translateExportStatus(h.value.status)}
										</Box>
									}
									secondary={h.value.comment}
									reduced
								/>
							</TableCell>
							<TableCell>
								<Box sx={{ textTransform: "uppercase", whiteSpace: "nowrap" }}>
									{h.createdBy.name}
								</Box>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</Box>
	);
};

type Mode = "view" | "approve" | "changeOther" | "revoke" | "undo" | "history" | "reject";

const SaleExportStatus = ({ sale, saleExport, setSale }: Props) => {
	const theme = useTheme();
	const [mode, setMode] = useState<Mode>("view");
	const [comment, setComment] = useState("");
	const [commentError, setCommentError] = useState(false);
	const [user, , hasRole] = useUser();

	const isAction = mode === "approve" || mode === "revoke" || mode === "undo" || mode === "reject";

	const lastApproverId = _.findLast(saleExport.statusHistory, (h => h.value.status === "FirstApproval"))?.createdBy.id;
	const canApprove = hasRole(UserRole.VehicleExportManager) &&
		(saleExport.status === "ReadyForApproval" ||
			(saleExport.status === "FirstApproval" && user.id !== lastApproverId));

	const actionPossible = saleExport.status !== "AwaitingDocuments" && saleExport.status !== "Cancelled" && saleExport.status !== "SecondApproval";

	const canRevoke = hasRole(UserRole.VehicleExportManager) && saleExport.status === "FirstApproval";
	const canUndo = hasRole(UserRole.VehicleExportManager) && saleExport.status === "Rejected";

	let statusColor: ThemeColor = getStatusColor(saleExport.status);

	const approve = async () => {
		let updatedSale = sale;

		if (saleExport.status === "ReadyForApproval" || saleExport.status === "FirstApproval") {
			const { data } = await axios.post<Sale>(`/api/sales/${sale.id}/export/approve`);
			updatedSale = data;
		}

		setSale(updatedSale);

		reset();
	};

	const revoke = async () => {
		try {
			const { data: updatedSale } = await axios.post<Sale>(`/api/sales/${sale.id}/export/revoke-approval`,
				{ comment: "" }
			);

			setSale(updatedSale);
		} finally {
			reset();
		}
	};

	const undoRejection = async () => {
		try {
			const { data: updatedSale } = await axios.post<Sale>(`/api/sales/${sale.id}/export/revoke-rejection`,
				{ comment: "Ablehnung zurückgezogen" }
			);

			setSale(updatedSale);
		} finally {
			reset();
		}
	};

	const reject = async () => {
		if (!comment) {
			setCommentError(true);
			return;
		}

		const { data: updatedSale } = await axios.post<Sale>(`/api/sales/${sale.id}/export/reject`,
			{ comment: comment }
		);

		setSale(updatedSale);

		reset();
	};

	const reset = () => {
		setCommentError(false);
		setComment("");
		setMode("view");
	};

	return (
		<List>
			<ListItem color="info" sx={{ mb: -1 }}>
				<ListItemActions>
					{actionPossible && !isAction && mode !== "history" && (
						<>
							<ListItemAction
								icon={<ThumbUpAlt />}
								disabled={!canApprove}
								onClick={() => setMode("approve")}
							/>
							<ListItemAction
								icon={<ThumbDownAlt />}
								disabled={!canApprove}
								onClick={() => setMode("reject")}
							/>
						</>
					)}
					{(isAction || mode === "history") && (
						<ListItemAction icon={<Clear />} onClick={reset} />
					)}
				</ListItemActions>
				<ListItemContent>
					<Typography variant="h6">Ausfuhrdokumente</Typography>
					{saleExport.lastComment && mode === "view" && (
						<Box mt={2}>
							<TextBlock
								primary={saleExport.lastComment}
								secondary="Letzter Kommentar"
							/>
						</Box>
					)}
					{mode === "approve" && (
						<Box
							mt={2}
							color={theme.palette.warning.light}
						>
							<Button
								color="inherit"
								variant="text"
								onClick={approve}
								startIcon={<ThumbUpAlt />}
							>
								Ausfuhrdokumente bestätigen
							</Button>
						</Box>
					)}
					{mode === "reject" && (
						<>
							<Box width="100%">
								<TextField
									value={comment}
									fullWidth
									error={commentError}
									name="comment"
									label="Notiz einfügen"
									onChange={(event) => setComment(event.target.value)}
									variant="standard"
									multiline
									required
								/>
							</Box>
							<Box
								mt={2}
								color={theme.palette.warning.light}
							>
								<Button
									color="inherit"
									variant="text"
									onClick={reject}
									startIcon={<ThumbDownAlt />}
								>
									Ausfuhrdokumente ablehnen
								</Button>
							</Box>
						</>
					)}
					{mode === "revoke" && (
						<Box mt={2} color={theme.palette.warning.light}>
							<Button
								color="inherit"
								variant="text"
								onClick={revoke}
								startIcon={<Backspace />}
							>
								Erste Bestätigung zurückziehen
							</Button>
						</Box>
					)}
					{mode === "undo" && (
						<Box mt={2} color={theme.palette.warning.light}>
							<Button
								color="inherit"
								variant="text"
								onClick={undoRejection}
								startIcon={<Backspace />}
							>
								Ablehnung zurückziehen
							</Button>
						</Box>
					)}
					{mode === "history" && (
						<Box mt={3} mb={1} width="100%">
							<StatusHistoryList history={saleExport.statusHistory} />
						</Box>
					)}
					<ListItemChips>
						<ListItemChip
							label={translateExportStatus(saleExport.status)}
							color={statusColor}
							active={mode === "approve"}
							onClick={canApprove ? () => setMode((m) => (m === "approve" ? "view" : "approve")) : null}
						/>
						{canRevoke && (
							<ListItemChip
								label="Erste Bestätigung zurückziehen"
								color="info"
								active={mode === "revoke"}
								onClick={() => setMode((m) => (m === "revoke" ? "view" : "revoke"))}
							/>
						)}
						{canUndo && (
							<ListItemChip
								label="Ablehnung zurückziehen"
								color="info"
								active={mode === "undo"}
								onClick={() => setMode((m) => (m === "revoke" ? "view" : "undo"))}
							/>
						)}
						<ListItemChip
							label="Historie"
							color="primary"
							active={mode === "history"}
							onClick={() => setMode((m) => (m === "history" ? "view" : "history"))}
						/>
					</ListItemChips>
				</ListItemContent>
			</ListItem>
		</List>
	);
};

export default SaleExportStatus;
